import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Plus, Ban, Copy, Check, Info, X, Shield, KeyRound, Users, AlertTriangle,
} from 'lucide-react';
import { AdminRoleWithUsage, getApiErrorMessage } from '../../../api/adminManagement';
import {
  AdminRoleWriteInput, RolePermissionKey,
  createAdminRole, updateAdminRole, setAdminRoleActive,
} from '../../../api/adminRoles';
import { ScopeBadge, ToggleSwitch, StatusPill, ErrorBanner } from './AdminManagementKit';
import config from '../../../configLoader';
import './RolesTab.css';

const EDU_HIDDEN_KEY = 'am_edu_hidden';

/**
 * Tab B — Admin Roles (master → editor). Approval Access WP9b.
 *
 * Left: a searchable list of role cards (Super marker, Inactive pill, up to 3
 * key-permission summary chips, "In use by N admins", selection ring). Right: the
 * role editor — header actions (Deactivate / Duplicate / Save role), a name/status
 * pair, a dismissible educational callout, and grouped permission toggle rows in
 * the exact group order 1–6.
 *
 * `.super-on`: when the Super Admin (Admin) toggle is ON the editor gets the
 * `super-on` class, which (via RolesTab.css, visual-only) dims + forces-on +
 * violet-tints every NON-access toggle and shows a "Granted" marker. The stored
 * booleans are never mutated — non-access switches keep their real `checked` value
 * and their onChange is suppressed while super-on, so toggling Admin off reveals the
 * real values and Save always sends only the real booleans.
 */

interface PermDef {
  k: RolePermissionKey; code: string; title: string; desc: string; scope: 'scoped' | 'global';
  /** false = this tenant doesn't have the feature: the row is not rendered, but the key
   *  stays in ALL_PERM_KEYS so create/update bodies still send it (as stored). Absent = shown. */
  visible?: boolean;
}

/** The rows a group renders (tenant-gated rows dropped). */
const shown = (defs: PermDef[]): PermDef[] => defs.filter((d) => d.visible !== false);

// Group 1 — Access level (violet panel). Not affected by .super-on.
const ACCESS_ROWS: PermDef[] = [
  { k: 'admin', code: 'Admin', title: 'Super Admin', scope: 'global',
    desc: 'Grants everything, everywhere. When on, every permission below is implicitly granted.' },
  { k: 'supervisor', code: 'Supervisor', title: 'Supervisor scope', scope: 'scoped',
    desc: 'Structural flag — anchors this admin to their linked employee’s hierarchy. Not itself a capability.' },
  { k: 'viewAllEmployees', code: 'ViewAllEmployees', title: 'View all employees', scope: 'global',
    desc: 'Sees every employee, regardless of hierarchy — without super-admin powers.' },
];

// Group 2 — Approvals (all scope-bound).
const APPROVALS: PermDef[] = [
  { k: 'viewTimesheets', code: 'ViewTimesheets', title: 'View timesheets', scope: 'scoped', desc: 'See timesheets for employees in scope' },
  { k: 'approveTimesheets', code: 'ApproveTimesheets', title: 'Approve timesheets', scope: 'scoped', desc: 'Advance manager / supervisor approval stages' },
  { k: 'payrollApproveTimesheets', code: 'PayrollApproveTimesheets', title: 'Payroll-approve timesheets', scope: 'scoped', desc: 'Move Payroll Pending → Approved' },
  { k: 'unlockTimesheet', code: 'UnlockTimesheet', title: 'Unlock timesheet', scope: 'scoped', desc: 'Unlock before payroll stages' },
  { k: 'unlockApprovedTimesheet', code: 'UnlockApprovedTimesheet', title: 'Unlock approved timesheet', scope: 'scoped', desc: 'Unlock at Payroll Pending / Payroll Approved' },
  { k: 'reassignTimesheetSupervisor', code: 'ReassignTimesheetSupervisor', title: 'Reassign timesheet supervisor', scope: 'scoped',
    desc: 'Move a single timesheet to a different supervisor for that pay period only' },
  { k: 'sendTimesheetReminders', code: 'SendTimesheetReminders', title: 'Send timesheet reminders', scope: 'global',
    desc: 'Push a reminder to every unsubmitted employee for the pay period (company-wide)' },
  { k: 'viewTimeOffRequests', code: 'ViewTimeOffRequests', title: 'View time off', scope: 'scoped', desc: 'See time off requests in scope' },
  { k: 'approveDenyTimeOffRequests', code: 'ApproveDenyTimeOffRequests', title: 'Approve / deny time off', scope: 'scoped', desc: 'Act on time off requests in scope' },
  { k: 'editApprovedTimeOffRequests', code: 'EditApprovedTimeOffRequests', title: 'Edit approved time off', scope: 'scoped',
    desc: 'Deny or change the hours of a time off request after it was approved (payroll corrections)' },
  { k: 'submitTimeOffForEmployee', code: 'SubmitTimeOffForEmployee', title: 'Submit time off for employees', scope: 'scoped',
    desc: 'Create a time off request on an employee’s behalf; it enters the approval queue' },
];

// Group 2.5 — Payroll Editing (scope-bound). Payroll Timesheet Modifications (TAS-59).
const PAYROLL_EDITING: PermDef[] = [
  { k: 'editTimesheetEntries', code: 'EditTimesheetEntries', title: 'Edit timesheet entries', scope: 'scoped',
    desc: 'Create, edit or delete an employee’s unsubmitted time entries and tasks as payroll' },
  { k: 'submitTimesheetForEmployee', code: 'SubmitTimesheetForEmployee', title: 'Submit timesheet for employee', scope: 'scoped',
    desc: 'Submit an unsubmitted timesheet on an employee’s behalf; recorded as a PAYROLL submission' },
  { k: 'editApprovedTimesheet', code: 'EditApprovedTimesheet', title: 'Edit approved timesheet', scope: 'scoped',
    desc: 'Modify a payroll-approved timesheet in place — future milestone; grant later' },
];

// Group 3 — Payroll & Operations (all org-wide).
const PAYROLL_OPS: PermDef[] = [
  { k: 'viewEmployees', code: 'ViewEmployees', title: 'View employees', scope: 'global', desc: 'Employee View page' },
  { k: 'viewAccountCodes', code: 'ViewAccountCodes', title: 'View account codes', scope: 'global', desc: '' },
  { k: 'managePayPeriods', code: 'ManagePayPeriods', title: 'Manage pay periods', scope: 'global', desc: '' },
  { k: 'manageLocations', code: 'ManageLocations', title: 'Manage locations', scope: 'global', desc: '' },
  { k: 'viewReports', code: 'ViewReports', title: 'View reports', scope: 'global', desc: '' },
  { k: 'viewMiscTasks', code: 'ViewMiscTasks', title: 'View misc tasks', scope: 'global', desc: '' },
  { k: 'approveMiscTasks', code: 'ApproveMiscTasks', title: 'Approve misc tasks (reds)', scope: 'scoped',
    desc: 'Act on the reds routed to this person as a 1st- or 2nd-level approver. The 2nd level writes the Vista description.' },
  { k: 'overrideMiscTaskApproval', code: 'OverrideMiscTaskApproval', title: 'Override misc task approval', scope: 'global',
    desc: 'Oversight: force-approve a stalled or unroutable red, deny at any stage, and correct the Vista description after approval.' },
  { k: 'viewTimesheetComments', code: 'ViewTimesheetComments', title: 'View timesheet comments', scope: 'global', desc: '' },
  { k: 'viewModifiedDuty', code: 'ViewModifiedDuty', title: 'Modified duty (view & edit)', scope: 'global',
    desc: 'See and edit the Modified Duty page' },
  { k: 'viewLASick', code: 'ViewLASick', title: 'View LA Sick', scope: 'global',
    desc: 'NPM: the LA Sick page — who worked Los Angeles jobs and whether Vista has them on CA LA SICK',
    visible: !!config.showLaSickPage },
  { k: 'manageSafetyMessages', code: 'ManageSafetyMessages', title: 'Manage safety messages', scope: 'global',
    desc: 'See and edit the weekly safety message' },
  { k: 'manageKioskDevices', code: 'ManageKioskDevices', title: 'Manage kiosk devices', scope: 'global',
    desc: 'Add kiosk tablets, generate enrollment codes, and deactivate or re-enroll them' },
  { k: 'processPayroll', code: 'ProcessPayroll', title: 'Process payroll', scope: 'global', desc: '' },
];

// Group 4 — Project Work (scope-bound).
const PROJECT_WORK: PermDef[] = [
  { k: 'viewProjectWork', code: 'ViewProjectWork', title: 'View project work', scope: 'scoped',
    desc: 'See Project Work for projects supervised by this admin or anyone they can view' },
];

// Group 4.5 — Work Orders (org-wide).
const WORK_ORDERS: PermDef[] = [
  { k: 'viewWorkOrders', code: 'ViewWorkOrders', title: 'View Work Orders', scope: 'global',
    desc: 'See the Work Orders page, PDFs, signed documents and photos' },
  { k: 'manageWorkOrders', code: 'ManageWorkOrders', title: 'Manage Work Orders (write)', scope: 'global',
    desc: 'Add PO#s, invoices, change orders, void / no-charge, signatures and uploads' },
  { k: 'manageWorkOrderRates', code: 'ManageWorkOrderRates', title: 'Manage Work Order pay rates', scope: 'global',
    desc: 'Set and change per-employee Work Order hourly pay rate overrides' },
];

// Group 5 — Interfaces (all org-wide).
const INTERFACES: PermDef[] = [
  { k: 'importTasks', code: 'ImportTasks', title: 'Import tasks', scope: 'global', desc: 'Task / piece import pages' },
  { k: 'exportAccounting', code: 'ExportAccounting', title: 'Export accounting', scope: 'global', desc: '' },
  { k: 'exportPayroll', code: 'ExportPayroll', title: 'Export payroll', scope: 'global', desc: '' },
];

// Group 6 — Other (existing fine-grained flags, all org-wide).
const OTHER: PermDef[] = [
  { k: 'manageCompanyPhones', code: 'ManageCompanyPhones', title: 'Manage company phones', scope: 'global',
    desc: 'Mark which employees have a company phone (affects WCDP phone reimbursement)' },
  { k: 'editEmployeeRole', code: 'EditEmployeeRole', title: 'Edit employee role', scope: 'global', desc: 'View the Employee Roles page (read-only) and change an employee’s role' },
  { k: 'manageEmployeeRoles', code: 'ManageEmployeeRoles', title: 'Manage employee roles', scope: 'global',
    desc: 'Create, edit, duplicate and deactivate roles on the Employee Roles page' },
  { k: 'activateInactivateEmployees', code: 'ActivateInactivateEmployees', title: 'Activate / inactivate employees', scope: 'global',
    desc: 'Manually activate or deactivate employee accounts from the Employees roster.' },
  { k: 'editMiscTaskAccountCodeAndDescription', code: 'EditMiscTaskAccountCodeAndDescription', title: 'Edit misc task code & description', scope: 'global', desc: '' },
  { k: 'viewProjectMap', code: 'ViewProjectMap', title: 'View project map', scope: 'global', desc: '' },
  { k: 'editProjectMap', code: 'EditProjectMap', title: 'Edit project map', scope: 'global', desc: '' },
  { k: 'viewAllProjectsMap', code: 'ViewAllProjectsMap', title: 'View all projects map', scope: 'global', desc: '' },
  { k: 'viewEmployeeEnrollmentPhotos', code: 'ViewEmployeeEnrollmentPhotos', title: 'View enrollment photos', scope: 'global', desc: '' },
  { k: 'editEmployeeEnrollmentPhotos', code: 'EditEmployeeEnrollmentPhotos', title: 'Edit enrollment photos', scope: 'global', desc: '' },
  { k: 'viewProjectsPage', code: 'ViewProjectsPage', title: 'View projects page', scope: 'global', desc: '' },
  { k: 'viewGpsExceptions', code: 'ViewGpsExceptions', title: 'View GPS exceptions', scope: 'global', desc: '' },
  { k: 'viewSelfieExceptions', code: 'ViewSelfieExceptions', title: 'View selfie exceptions', scope: 'global',
    desc: 'The Selfie Exceptions page — compare punch selfies against enrollment photos and mark them reviewed.' },
];

const ALL_PERM_KEYS: RolePermissionKey[] = [
  ...ACCESS_ROWS, ...APPROVALS, ...PAYROLL_EDITING, ...PAYROLL_OPS, ...PROJECT_WORK, ...WORK_ORDERS, ...INTERFACES, ...OTHER,
].map((d) => d.k);

interface RoleDraft {
  adminEmployeeRoleID: number | null; // null = new, unsaved
  name: string;
  active: boolean;
  perms: Record<RolePermissionKey, boolean>;
  inUseCount: number;
}

function permsFromRole(role: AdminRoleWithUsage): Record<RolePermissionKey, boolean> {
  const perms = {} as Record<RolePermissionKey, boolean>;
  const src = role as unknown as Record<string, unknown>;
  ALL_PERM_KEYS.forEach((k) => { perms[k] = !!src[k]; });
  return perms;
}
function blankPerms(): Record<RolePermissionKey, boolean> {
  const perms = {} as Record<RolePermissionKey, boolean>;
  ALL_PERM_KEYS.forEach((k) => { perms[k] = false; });
  return perms;
}
function draftFromRole(role: AdminRoleWithUsage): RoleDraft {
  return {
    adminEmployeeRoleID: role.adminEmployeeRoleID,
    name: role.name,
    active: role.active,
    perms: permsFromRole(role),
    inUseCount: role.inUseCount,
  };
}

/** Up to 3 human-friendly key-permission summary chips for a role card. */
function deriveChips(role: AdminRoleWithUsage): string[] {
  if (role.admin) return ['Everything, everywhere'];
  const chips: string[] = [];
  if (role.supervisor) chips.push('Supervisor scope');
  if (role.viewAllEmployees) chips.push('View all employees');
  if (role.payrollApproveTimesheets) chips.push('Payroll approve');
  else if (role.approveTimesheets) chips.push('Approve timesheets');
  else if (role.viewTimesheets) chips.push('View timesheets');
  if (role.approveDenyTimeOffRequests) chips.push('Approve time off');
  else if (role.viewTimeOffRequests) chips.push('View time off');
  if (role.processPayroll) chips.push('Process payroll');
  if (role.exportPayroll || role.exportAccounting) chips.push('Exports');
  if (role.viewReports) chips.push('Reports');
  if (role.viewProjectWork) chips.push('Project work');
  if (role.viewEmployees) chips.push('View employees');
  if (role.viewTimesheetComments) chips.push('Comments');
  return chips.length ? chips.slice(0, 3) : ['View-only'];
}

interface Props {
  roles: AdminRoleWithUsage[];
  /** Re-fetch the role list (also refreshes the tab-bar roles badge). Returns after state settles. */
  onRolesChanged: () => Promise<void>;
  showToast: (msg: string, isError?: boolean) => void;
}

const RolesTab: React.FC<Props> = ({ roles, onRolesChanged, showToast }) => {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [draft, setDraft] = useState<RoleDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState<string | null>(null);

  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const setDirtyBoth = (v: boolean) => { dirtyRef.current = v; setDirty(v); };

  const [eduHidden, setEduHidden] = useState<boolean>(() => {
    try { return localStorage.getItem(EDU_HIDDEN_KEY) === '1'; } catch { return false; }
  });

  const superOn = !!draft?.perms.admin;

  // ── Selection ────────────────────────────────────────────────────────────────
  const selectRole = (role: AdminRoleWithUsage) => {
    setSelectedId(role.adminEmployeeRoleID);
    setIsNew(false);
    setDraft(draftFromRole(role));
    setEditorError(null);
    setDirtyBoth(false);
  };

  // Confirm before abandoning unsaved edits when switching away.
  const guardSwitch = (): boolean => {
    if (!dirtyRef.current) return true;
    // eslint-disable-next-line no-alert
    return window.confirm('You have unsaved changes to this role. Discard them?');
  };

  const attemptSelect = (role: AdminRoleWithUsage) => {
    if (role.adminEmployeeRoleID === selectedId && !isNew) return;
    if (!guardSwitch()) return;
    selectRole(role);
  };

  const startNewRole = () => {
    if (!guardSwitch()) return;
    setSelectedId(null);
    setIsNew(true);
    setDraft({ adminEmployeeRoleID: null, name: '', active: true, perms: blankPerms(), inUseCount: 0 });
    setEditorError(null);
    setDirtyBoth(false);
  };

  const duplicateRole = () => {
    if (!draft) return;
    if (!guardSwitch()) return;
    setSelectedId(null);
    setIsNew(true);
    setDraft({
      adminEmployeeRoleID: null,
      name: `${draft.name} (copy)`.trim(),
      active: true,
      perms: { ...draft.perms },
      inUseCount: 0,
    });
    setEditorError(null);
    setDirtyBoth(true);
  };

  // Auto-select the first role when nothing is selected (README §Interactions).
  useEffect(() => {
    if (selectedId === null && !isNew && roles.length > 0) {
      selectRole(roles[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, selectedId, isNew]);

  // Keep the editor in sync with an external refresh of the selected role, but never
  // clobber in-progress edits (dirty) — after a save dirty is false so this re-hydrates.
  useEffect(() => {
    if (selectedId === null || dirtyRef.current) return;
    const role = roles.find((r) => r.adminEmployeeRoleID === selectedId);
    if (role) setDraft(draftFromRole(role));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, selectedId]);

  // ── Editing ────────────────────────────────────────────────────────────────
  const setPerm = (k: RolePermissionKey, v: boolean) => {
    setDraft((d) => (d ? { ...d, perms: { ...d.perms, [k]: v } } : d));
    setDirtyBoth(true);
  };
  const setName = (name: string) => { setDraft((d) => (d ? { ...d, name } : d)); setDirtyBoth(true); };
  const setActive = (active: boolean) => { setDraft((d) => (d ? { ...d, active } : d)); setDirtyBoth(true); };

  const dismissEdu = () => {
    setEduHidden(true);
    try { localStorage.setItem(EDU_HIDDEN_KEY, '1'); } catch { /* ignore */ }
  };

  // ── Save ───────────────────────────────────────────────────────────────────
  const canSave = !!draft && draft.name.trim() !== '' && !saving;

  const handleSave = async () => {
    if (!draft || !canSave) return;
    setSaving(true);
    setEditorError(null);
    const payload = {
      name: draft.name.trim(),
      active: draft.active,
      ...draft.perms,
    } as AdminRoleWriteInput;
    try {
      if (draft.adminEmployeeRoleID === null) {
        const newID = await createAdminRole(payload);
        setDirtyBoth(false);
        await onRolesChanged();
        setSelectedId(newID);
        setIsNew(false);
        showToast('Role created.');
      } else {
        await updateAdminRole(draft.adminEmployeeRoleID, payload);
        setDirtyBoth(false);
        await onRolesChanged();
        showToast('Role saved.');
      }
    } catch (err) {
      // Surface every server 400 (duplicate name, last-super-admin demotion) as a
      // blocking ErrorBanner in the editor.
      setEditorError(getApiErrorMessage(err, 'Could not save the role.'));
    } finally {
      setSaving(false);
    }
  };

  // ── Deactivate ───────────────────────────────────────────────────────────────
  const deactivateDisabled = !draft || isNew || draft.perms.admin || draft.inUseCount > 0 || !draft.active;
  const deactivateTip = !draft ? undefined
    : draft.perms.admin ? "The Super Admin role can't be deactivated."
      : draft.inUseCount > 0 ? `In use by ${draft.inUseCount} admin${draft.inUseCount === 1 ? '' : 's'} — reassign them first.`
        : isNew ? 'Save the role before deactivating.'
          : !draft.active ? 'This role is already inactive.'
            : undefined;

  const handleDeactivate = async () => {
    if (!draft || draft.adminEmployeeRoleID === null || deactivateDisabled) return;
    setSaving(true);
    setEditorError(null);
    try {
      await setAdminRoleActive(draft.adminEmployeeRoleID, false);
      setDirtyBoth(false);
      await onRolesChanged();
      showToast('Role deactivated.');
    } catch (err) {
      setEditorError(getApiErrorMessage(err, 'Could not deactivate the role.'));
    } finally {
      setSaving(false);
    }
  };

  // ── Filtered card list ─────────────────────────────────────────────────────
  const filteredRoles = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return roles;
    return roles.filter((r) => r.name.toLowerCase().includes(q));
  }, [roles, search]);

  // ── Render helpers ───────────────────────────────────────────────────────────
  const renderRow = (def: PermDef, isAccess: boolean) => {
    const checked = draft ? draft.perms[def.k] : false;
    const locked = !isAccess && superOn; // suppress interaction while implied-granted
    return (
      <div className="toggle-row" key={def.k}>
        <div className="tr-main">
          <div className="tr-title">
            <span className="tt">{def.title}</span>
            <ScopeBadge scope={def.scope} />
            <span className="granted-note"><Check />Granted</span>
          </div>
          <div className="tr-desc">{def.desc ? `${def.desc} ` : ''}<code>{def.code}</code></div>
        </div>
        <ToggleSwitch
          checked={checked}
          onChange={locked ? undefined : (v) => setPerm(def.k, v)}
          ariaLabel={def.title}
        />
      </div>
    );
  };

  const editorEmpty = !draft;

  return (
    <>
      {/* Controls bar */}
      <div className="toolbar">
        <div className="field search">
          <span className="flab">Search</span>
          <div className="searchwrap">
            <Search />
            <input
              className="input"
              placeholder="Search roles…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
        <div className="field push">
          <button className="btn primary" type="button" onClick={startNewRole}><Plus />Add Role</button>
        </div>
      </div>

      <div className="role-layout">
        {/* Role card list */}
        <div className="role-list">
          {filteredRoles.length === 0 ? (
            <div className="card"><div className="empty" style={{ padding: '40px 20px' }}>
              <div className="et">No roles match</div>
              <p>Adjust the search or add a new role.</p>
            </div></div>
          ) : filteredRoles.map((r) => {
            const selected = !isNew && r.adminEmployeeRoleID === selectedId;
            return (
              <button
                key={r.adminEmployeeRoleID}
                type="button"
                className={`role-card${selected ? ' on' : ''}${r.active ? '' : ' dim'}`}
                onClick={() => attemptSelect(r)}
              >
                <div className="rc-top">
                  <span className="rc-name">{r.name}</span>
                  {r.admin && <span className="super-mark"><Shield />Super</span>}
                  {!r.active && <StatusPill variant="inactive" />}
                </div>
                <div className="rc-chips">
                  {deriveChips(r).map((c) => <span key={c} className="perm-chip">{c}</span>)}
                </div>
                <div className="rc-use">
                  <Users />In use by <b>{r.inUseCount}</b> admin{r.inUseCount === 1 ? '' : 's'}
                </div>
              </button>
            );
          })}
        </div>

        {/* Role editor */}
        {editorEmpty ? (
          <div className="role-editor"><div className="empty roles-empty">
            <div className="eic"><KeyRound /></div>
            <div className="et">No role selected</div>
            <p>Select a role from the list, or add a new one to define its permissions.</p>
          </div></div>
        ) : (
          <div className={`role-editor${superOn ? ' super-on' : ''}`}>
            {/* Header */}
            <div className="re-head">
              <span className={`re-ic${draft!.perms.admin ? ' super' : ''}`}>
                {draft!.perms.admin ? <Shield /> : <KeyRound />}
              </span>
              <div>
                <h2>{draft!.name.trim() || (isNew ? 'New role' : 'Untitled role')}</h2>
                <div className="re-sub">
                  {draft!.inUseCount} admin{draft!.inUseCount === 1 ? '' : 's'} · {draft!.active ? 'Active' : 'Inactive'}
                </div>
              </div>
              <div className="re-actions">
                {deactivateDisabled ? (
                  <span className="tipwrap" data-tip={deactivateTip}>
                    <button className="btn sm" type="button" disabled><Ban />Deactivate</button>
                  </span>
                ) : (
                  <button className="btn sm" type="button" onClick={handleDeactivate}><Ban />Deactivate</button>
                )}
                <button className="btn sm" type="button" onClick={duplicateRole}><Copy />Duplicate</button>
                <button className="btn primary sm" type="button" onClick={handleSave} disabled={!canSave}>
                  <Check />{saving ? 'Saving…' : 'Save role'}
                </button>
              </div>
            </div>

            {/* Body */}
            <div className="re-body">
              {editorError && <div style={{ marginTop: 16 }}><ErrorBanner>{editorError}</ErrorBanner></div>}

              {/* Role name / status */}
              <div className="frow2">
                <div className="frow" style={{ margin: 0 }}>
                  <label>Role name<span className="req">*</span></label>
                  <input className="input" value={draft!.name} onChange={(e) => setName(e.target.value)} />
                </div>
                <div className="frow" style={{ margin: 0 }}>
                  <label>Status</label>
                  <select
                    className="select"
                    value={draft!.active ? 'active' : 'inactive'}
                    onChange={(e) => setActive(e.target.value === 'active')}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              {/* Educational callout */}
              {!eduHidden && (
                <div className="edu-note">
                  <Info />
                  <span>
                    <b>Approval permissions</b> apply to the employees in this admin’s scope (their hierarchy
                    plus any delegations). <b>Org-wide permissions</b> are not limited by scope.
                  </span>
                  <button className="x" type="button" aria-label="Dismiss" onClick={dismissEdu}><X /></button>
                </div>
              )}

              {/* Group 1 — Access level (violet panel) */}
              <div className="perm-group access">
                <div className="pg-label">Access level</div>
                {shown(ACCESS_ROWS).map((def) => renderRow(def, true))}
              </div>

              {/* Group 2 — Approvals (scope-bound) */}
              <div className="perm-group">
                <div className="pg-label">Approvals <span className="pg-badge"><ScopeBadge scope="scoped" /></span></div>
                {shown(APPROVALS.slice(0, 5)).map((def) => renderRow(def, false))}
                <div className="unlock-warn">
                  <AlertTriangle />
                  <span>Unlocking <b>deletes the submitted timesheet</b> so it can be redone. This cannot be undone.</span>
                </div>
                {shown(APPROVALS.slice(5)).map((def) => renderRow(def, false))}
              </div>

              {/* Group 2.5 — Payroll Editing (scope-bound) */}
              <div className="perm-group">
                <div className="pg-label">Payroll Editing <span className="pg-badge"><ScopeBadge scope="scoped" /></span></div>
                {shown(PAYROLL_EDITING).map((def) => renderRow(def, false))}
              </div>

              {/* Group 3 — Payroll & Operations (org-wide) */}
              <div className="perm-group">
                <div className="pg-label">Payroll &amp; Operations <span className="pg-badge"><ScopeBadge scope="global" /></span></div>
                {shown(PAYROLL_OPS).map((def) => renderRow(def, false))}
              </div>

              {/* Group 4 — Project Work (scope-bound) */}
              <div className="perm-group">
                <div className="pg-label">Project Work <span className="pg-badge"><ScopeBadge scope="scoped" /></span></div>
                {shown(PROJECT_WORK).map((def) => renderRow(def, false))}
              </div>

              {/* Group 4.5 — Work Orders (org-wide) */}
              <div className="perm-group">
                <div className="pg-label">Work Orders <span className="pg-badge"><ScopeBadge scope="global" /></span></div>
                {shown(WORK_ORDERS).map((def) => renderRow(def, false))}
              </div>

              {/* Group 5 — Interfaces (org-wide) */}
              <div className="perm-group">
                <div className="pg-label">Interfaces <span className="pg-badge"><ScopeBadge scope="global" /></span></div>
                {shown(INTERFACES).map((def) => renderRow(def, false))}
              </div>

              {/* Group 6 — Other (org-wide) */}
              <div className="perm-group">
                <div className="pg-label">Other <span className="pg-badge"><ScopeBadge scope="global" /></span></div>
                {shown(OTHER).map((def) => renderRow(def, false))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default RolesTab;
